import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  ListItem,
  SimpleGrid,
  Spinner,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import PageHeader from "../components/PageHeader";
import PoolCardScroller from "../components/PoolCardScroller";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getMyPools, getAllPools, joinPool, createPool } from "../api/pools";
import { getPlSetupStatus } from "../api/premierLeague";
import plLogo from "../assets/logos/premier-league.png";
import { usePlSeason } from "../hooks/usePlSeason";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";

export default function PremierLeagueHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { seasonId: LIVE_SEASON_ID } = usePlSeason();

  const [myPools, setMyPools] = useState([]);
  const [allPools, setAllPools] = useState([]);
  const [loadingPools, setLoadingPools] = useState(true);
  const [poolError, setPoolError] = useState("");

  const [setupAvailable, setSetupAvailable] = useState(false);
  const [seasonStarted, setSeasonStarted] = useState(false);
  const [nextSeasonLabel, setNextSeasonLabel] = useState(null);
  const [nextSeasonId, setNextSeasonId] = useState(null);

  const [creatingPool, setCreatingPool] = useState(false);
  const [createError, setCreateError] = useState("");
  const [joiningPoolId, setJoiningPoolId] = useState(null);

  const [newPoolName, setNewPoolName] = useState("");
  const [newPoolDepth, setNewPoolDepth] = useState("");

  useEffect(() => {
    if (!LIVE_SEASON_ID) return;
    async function init() {
      let sid = LIVE_SEASON_ID;
      try {
        const s = await getPlSetupStatus();
        setSetupAvailable(s.setup_available);
        setSeasonStarted(!!s.season_started);
        setNextSeasonLabel(s.next_season_label);
        const nid = s.next_season_id ?? null;
        setNextSeasonId(nid);
        if (s.setup_available && nid) sid = nid;
      } catch (_) {}
      await loadPools(sid);
    }
    init();
  }, [LIVE_SEASON_ID]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadPools(seasonId) {
    const sid = seasonId ?? LIVE_SEASON_ID;
    setLoadingPools(true);
    setPoolError("");

    try {
      const [mine, all] = await Promise.all([
        getMyPools(sid),
        getAllPools(sid),
      ]);

      setMyPools(mine);
      setAllPools(all);
    } catch (err) {
      setPoolError(err?.response?.data?.detail || "Failed to load pools.");
      setMyPools([]);
      setAllPools([]);
    } finally {
      setLoadingPools(false);
    }
  }

  const joinablePools = useMemo(() => {
    // allPools is already scoped to the correct season (next season during the
    // setup window, current live season otherwise) via loadPools(sid) in init().
    const myPoolIds = new Set(myPools.map((p) => p.id));
    return allPools.filter((p) => !myPoolIds.has(p.id));
  }, [myPools, allPools]);

  function handleBackToGameHome() {
    navigate("/");
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  function handleOpenPool(poolId, seasonId) {
    navigate(`/premier-league/current/${poolId}`, { state: { seasonId } });
  }

  async function handleJoinPool(poolId) {
    setJoiningPoolId(poolId);
    setPoolError("");

    try {
      await joinPool(LIVE_SEASON_ID, poolId);
      await loadPools();
      navigate(`/premier-league/current/${poolId}`, { state: { seasonId: LIVE_SEASON_ID } });
    } catch (err) {
      setPoolError(err?.response?.data?.detail || "Failed to join pool.");
    } finally {
      setJoiningPoolId(null);
    }
  }

  const creationOpen = setupAvailable;

  async function handleCreatePool() {
    if (!creationOpen) return;

    const trimmedName = newPoolName.trim();
    if (!trimmedName) {
      setCreateError("Pool name is required.");
      return;
    }

    const parsedDepth = newPoolDepth ? Number(newPoolDepth) : null;
    if (parsedDepth !== null && (!Number.isInteger(parsedDepth) || parsedDepth < 1)) {
      setCreateError("Pool depth must be a whole number greater than 0.");
      return;
    }

    setCreatingPool(true);
    setCreateError("");

    try {
      const seasonIdForCreate = (setupAvailable && nextSeasonId) ? nextSeasonId : LIVE_SEASON_ID;
      const createdPool = await createPool(seasonIdForCreate, {
        name: trimmedName,
        pool_depth: parsedDepth,
      });
      setNewPoolName("");
      setNewPoolDepth("");
      navigate(`/premier-league/setup/${createdPool.id}`, { state: { seasonId: createdPool.season_id } });
    } catch (err) {
      setCreateError(err?.response?.data?.detail || "Failed to create pool.");
    } finally {
      setCreatingPool(false);
    }
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white" overflowX="hidden">
      <PageHeader
        title="PL PICK 'EM LOBBY"
        onBack={handleBackToGameHome}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={10}>
        <VStack align="stretch" spacing={8}>
          <VStack align="start" spacing={2}>
            <Text fontSize="lg" color="whiteAlpha.900">
              Welcome to the PL Pick 'Em lobby. Enter your current game, view open pools, or start a new pool.
            </Text>
            {setupAvailable && nextSeasonLabel && (
              <Alert status="success" borderRadius="md" mt={1} color="green.900">
                <AlertIcon flexShrink={0} />
                <Text ml={2}>
                  The <strong>{nextSeasonLabel}</strong> season is available for set up — create your pool below!
                </Text>
              </Alert>
            )}
          </VStack>

          {poolError ? (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {poolError}
            </Alert>
          ) : null}

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            {/* Your Active Games */}
            <Box
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={8}
              minH="260px"
              minW="0"
              position="relative"
              overflow="hidden"
            
              opacity={!loadingPools && myPools.length === 0 ? 0.5 : 1}
            >
              {/* Watermark */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
                opacity={0.06}
                pointerEvents="none"
                zIndex={0}
              >
                <img src={plLogo} alt="" style={{ width: "300px" }} />
              </Box>

              <VStack align="stretch" spacing={4} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>
                  Your Active Games
                </Heading>

                <Text color="gray.700">
                  Pools you belong to for the current Premier League season.
                </Text>

                {loadingPools ? (
                  <Flex flex="1" align="center" justify="center">
                    <Spinner />
                  </Flex>
                ) : myPools.length === 0 ? (
                  <Flex flex="1" align="center" justify="center">
                    <Text fontSize="lg" color="gray.500" textAlign="center">
                      You are not currently in any pools.
                    </Text>
                  </Flex>
                ) : (
                  <Box maxH={{ base: "240px", sm: "unset" }} overflowY={{ base: "auto", sm: "visible" }} mt={1}>
                    {/* Mobile: compact list */}
                    <VStack align="stretch" spacing={0} display={{ base: "flex", sm: "none" }}>
                      {myPools.map((pool) => (
                        <Flex key={pool.id} align="center" py={2} borderBottom="1px solid" borderColor="gray.100">
                          <Text fontWeight="semibold" fontSize="sm" flex="1" mr={3} noOfLines={1}>{pool.name}</Text>
                          <HStack spacing={3} flexShrink={0}>
                            {(pool.owner_user_id === user?.id || pool.current_user_is_co_admin) && (
                              <Button variant="link" size="sm" color={TOP_BAR_BLUE} onClick={() => navigate(`/premier-league/setup/${pool.id}`, { state: { seasonId: pool.season_id } })}>Edit</Button>
                            )}
                            <Button variant="link" size="sm" color={TOP_BAR_BLUE} fontWeight="bold" onClick={() => handleOpenPool(pool.id, pool.season_id)}>Open</Button>
                          </HStack>
                        </Flex>
                      ))}
                    </VStack>
                    {/* Desktop: horizontally scrolling cards */}
                    <PoolCardScroller display={{ base: "none", sm: "block" }}>
                      {myPools.map((pool) => (
                        <Box key={pool.id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} bg="gray.50">
                          <VStack align="center" spacing={2}>
                            <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                            <Text fontSize="sm" color="gray.600" textAlign="center">Participants: {pool.user_count}</Text>
                            {pool.total_pot != null && (
                              <Text fontSize="sm" color="gray.600" textAlign="center">Total Pot: ${pool.total_pot}</Text>
                            )}
                            <HStack spacing={2} justify="center">
                              {(pool.owner_user_id === user?.id || pool.current_user_is_co_admin) && (
                                <Button size="sm" variant="outline" borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE} onClick={() => navigate(`/premier-league/setup/${pool.id}`, { state: { seasonId: pool.season_id } })} _hover={{ bg: "gray.100" }}>Edit Pool</Button>
                              )}
                              <Button size="sm" bg={TOP_BAR_BLUE} color="white" onClick={() => handleOpenPool(pool.id, pool.season_id)} _hover={{ bg: "#00004a" }}>Open Pool</Button>
                            </HStack>
                          </VStack>
                        </Box>
                      ))}
                    </PoolCardScroller>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Join an Open Pool */}
            <Box
              bg="white"
              color="black"
              border={`3px solid ${TOP_BAR_BLUE}`}
              borderRadius="lg"
              boxShadow="lg"
              p={8}
              minH="260px"
              minW="0"
              opacity={creationOpen ? 1 : 0.6}
              position="relative"
              overflow="hidden"
            >
              {/* Watermark */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                overflow="hidden"
                display="flex"
                alignItems="center"
                justifyContent="center"
                opacity={0.06}
                pointerEvents="none"
                zIndex={0}
              >
                <img src={plLogo} alt="" style={{ width: "300px" }} />
              </Box>

              <VStack align="stretch" spacing={5} h="100%" position="relative" zIndex={1}>
                <Heading size="md" color={TOP_BAR_BLUE}>
                  Join an Open Pool
                </Heading>

                {seasonStarted ? (
                  <Alert status="warning" borderRadius="md">
                    <AlertIcon />
                    Joining a pool is not available once the season has started.
                  </Alert>
                ) : (
                  <Text color="gray.700">
                    Available pools for the current season.
                  </Text>
                )}

                {!seasonStarted && (
                  loadingPools ? (
                    <Flex flex="1" align="center" justify="center">
                      <Spinner />
                    </Flex>
                  ) : joinablePools.length === 0 ? (
                    <Flex flex="1" align="center" justify="center">
                      <Text fontSize="lg" color="gray.500" textAlign="center">
                        No open pools available right now.
                      </Text>
                    </Flex>
                  ) : (
                    <PoolCardScroller>
                      {joinablePools.map((pool) => (
                        <Box
                          key={pool.id}
                          border="1px solid"
                          borderColor="gray.200"
                          borderRadius="md"
                          p={4}
                          bg="gray.50"
                        >
                          <VStack align="center" spacing={2}>
                            <Text fontWeight="bold" textAlign="center">{pool.name}</Text>
                            <Text fontSize="sm" color="gray.600" textAlign="center">
                              Pool Depth: {pool.pool_depth}
                            </Text>
                            <Text fontSize="sm" color="gray.600" textAlign="center">
                              Users: {pool.user_count}
                            </Text>
                            {pool.total_pot != null && (
                              <Text fontSize="sm" color="gray.600" textAlign="center">
                                Total Pot: ${pool.total_pot}
                              </Text>
                            )}
                            <Button
                              size="sm"
                              bg={TOP_BAR_BLUE}
                              color="white"
                              onClick={() => handleJoinPool(pool.id)}
                              isLoading={joiningPoolId === pool.id}
                              _hover={{ bg: "#00004a" }}
                            >
                              Join Pool
                            </Button>
                          </VStack>
                        </Box>
                      ))}
                    </PoolCardScroller>
                  )
                )}
              </VStack>
            </Box>
          </SimpleGrid>

          <Box
            bg="white"
            color="black"
            border={`3px solid ${TOP_BAR_BLUE}`}
            borderRadius="lg"
            boxShadow="lg"
            p={8}
          >
            <VStack align="stretch" spacing={8}>
              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>
                  Want to Start a New Pool?
                </Heading>

                {seasonStarted ? (
                  <Alert status="warning" borderRadius="md" mb={4}>
                    <AlertIcon />
                    New games can&apos;t be created mid season.
                  </Alert>
                ) : null}
              </Box>

              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>
                  Rules
                </Heading>
                <UnorderedList spacing={3} pl={5}>
                  <ListItem>
                    Pick <strong>one team each week</strong> and assign it <strong>1-5 confidence points.</strong>
                  </ListItem>
                  <ListItem>
                    Picks must be made before kickoff. Once a fixture kicks off, it is locked.
                  </ListItem>
                  <ListItem>
                    A team may only be picked <strong>once per half of the season</strong>.
                  </ListItem>
                  <ListItem>
                    You may only pick <strong>against</strong> the same team a limited number of times — the pool owner sets the limit.
                  </ListItem>
                  <ListItem>
                    Miss a pick and you get the team with the worst matchup based on the current table at 5 confidence points.
                  </ListItem>
                  <ListItem>
                    Predict how the table finishes. Predictions lock before Matchweek 4 and are scored on their own leaderboard.
                  </ListItem>
                </UnorderedList>
              </Box>

              <Divider borderColor="gray.300" />

              <Box>
                <Heading size="md" mb={3} color={TOP_BAR_BLUE}>
                  Points Structure
                </Heading>
                <UnorderedList spacing={3} pl={5}>
                  <ListItem>
                    If your selected team wins, you earn the confidence points selected for the pick.
                  </ListItem>
                  <ListItem>
                    If your selected team loses, you lose the confidence points selected for the pick.
                  </ListItem>
                  <ListItem>
                    If your selected team draws, you receive no points for that matchweek pick.
                  </ListItem>
                  <ListItem>
                    <strong>Clean Sheet Bonus:</strong> win and keep a clean sheet for an extra 2 points.
                  </ListItem>
                  <ListItem>
                    Most points wins. Ties are broken by goal differential.
                  </ListItem>
                </UnorderedList>
              </Box>

              <Divider borderColor="gray.300" />

              <Box opacity={creationOpen ? 1 : 0.5}>
                <VStack align="stretch" spacing={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl isDisabled={!creationOpen}>
                      <FormLabel>Pool Name</FormLabel>
                      <Input
                        value={newPoolName}
                        onChange={(e) => setNewPoolName(e.target.value)}
                        placeholder="Enter pool name"
                      />
                    </FormControl>

                    <FormControl isDisabled={!creationOpen}>
                      <FormLabel>Pool Depth ($)</FormLabel>
                      <Input
                        type="number"
                        min={1}
                        value={newPoolDepth}
                        onChange={(e) => setNewPoolDepth(e.target.value)}
                        placeholder="Optional buy-in amount"
                      />
                    </FormControl>
                  </SimpleGrid>

                  {createError && (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />{createError}
                    </Alert>
                  )}
                  <HStack spacing={4} flexWrap="wrap">
                    <Button
                      bg={TOP_BAR_BLUE}
                      color="white"
                      isDisabled={!creationOpen}
                      isLoading={creatingPool}
                      onClick={handleCreatePool}
                      _hover={{ bg: "#00004a" }}
                    >
                      Start New Pool
                    </Button>
                  </HStack>
                </VStack>

                {!creationOpen && !seasonStarted && (
                  <Text mt={4} color="gray.700">
                    Once next season setup is available, this page will allow users to create a
                    new pool before the season begins.
                  </Text>
                )}
              </Box>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
